import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import HtmlEditor from "../../components/admin/HtmlEditor";
import ImageUpload from "../../components/admin/ImageUpload";

const blank = {
  name: "",
  slug: "",
  tagline: "",
  short_description: "",
  long_description: "",
  category: "",
  color: "orange",
  icon: "Box",
  hero_image: "",
  logo_image: "",
  external_url: "",
  features: [],
  screenshots: [],
  pricing: [],
  faqs: [],
  cta_title: "",
  cta_description: "",
  cta_button_label: "Get Started",
  seo: { title: "", description: "", keywords: "", og_image: "", og_type: "product", og_url: "", twitter_card: "summary_large_image", canonical_url: "", json_ld: "" },
  is_published: true,
  order: 0,
};

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">{label}</label>
    {children}
    {hint && <div className="text-xs text-zinc-400 mt-1">{hint}</div>}
  </div>
);

const inputCls = "w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500 text-sm";

const AdminProductEditor = () => {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/products/${id}`).then((r) => setForm({ ...blank, ...r.data, seo: { ...blank.seo, ...(r.data.seo || {}) } })).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updSeo = (k, v) => setForm((f) => ({ ...f, seo: { ...f.seo, [k]: v } }));

  const updArrItem = (arr, idx, k, v) => {
    const next = [...form[arr]];
    next[idx] = { ...next[idx], [k]: v };
    upd(arr, next);
  };
  const addArrItem = (arr, item) => upd(arr, [...form[arr], item]);
  const removeArrItem = (arr, idx) => upd(arr, form[arr].filter((_, i) => i !== idx));

  const updScreenshot = (idx, v) => upd("screenshots", form.screenshots.map((s, i) => (i === idx ? v : s)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/products", form);
        toast.success("Product created");
        navigate(`/admin/products/${data.id}`);
      } else {
        await api.put(`/admin/products/${id}`, form);
        toast.success("Product updated");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500">Loading…</div>;

  return (
    <form onSubmit={submit} data-testid="admin-product-editor" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">{isNew ? "New product" : `Edit: ${form.name}`}</h1>
          <p className="text-sm text-zinc-500 mt-1">All fields render dynamically on the public product page.</p>
        </div>
        <button type="submit" disabled={saving} data-testid="admin-product-save" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
      </div>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Name *"><input className={inputCls} value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="product-name" /></Field>
        <Field label="Slug" hint="URL path (auto-generated if blank)"><input className={inputCls} value={form.slug} onChange={(e) => upd("slug", e.target.value)} data-testid="product-slug" /></Field>
        <Field label="Tagline"><input className={inputCls} value={form.tagline} onChange={(e) => upd("tagline", e.target.value)} data-testid="product-tagline" /></Field>
        <Field label="Category"><input className={inputCls} value={form.category} onChange={(e) => upd("category", e.target.value)} data-testid="product-category" /></Field>
        <Field label="External URL"><input className={inputCls} value={form.external_url} onChange={(e) => upd("external_url", e.target.value)} data-testid="product-external" /></Field>
        <Field label="Color accent">
          <select className={inputCls} value={form.color} onChange={(e) => upd("color", e.target.value)} data-testid="product-color">
            <option value="orange">Orange</option>
            <option value="green">Green</option>
          </select>
        </Field>
        <Field label="Icon name (lucide-react)" hint="e.g. Box, Wallet, Users, Code2"><input className={inputCls} value={form.icon} onChange={(e) => upd("icon", e.target.value)} data-testid="product-icon" /></Field>
        <div className="md:col-span-2"><ImageUpload label="Hero image" value={form.hero_image} onChange={(v) => upd("hero_image", v)} testId="product-hero-upload" /></div>
        <div className="md:col-span-2"><ImageUpload label="Logo image" value={form.logo_image} onChange={(v) => upd("logo_image", v)} testId="product-logo-upload" /></div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Short description (HTML) <span className="text-zinc-400 font-normal normal-case tracking-normal">— shown in the hero section</span></label>
          <HtmlEditor value={form.short_description} onChange={(v) => upd("short_description", v)} minHeight={200} testId="product-short-desc" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Long description (HTML) <span className="text-zinc-400 font-normal normal-case tracking-normal">— shown in the Overview section</span></label>
          <HtmlEditor value={form.long_description} onChange={(v) => upd("long_description", v)} minHeight={260} testId="product-long-desc" />
        </div>
        <div className="flex items-center gap-3">
          <input id="published" type="checkbox" checked={form.is_published} onChange={(e) => upd("is_published", e.target.checked)} data-testid="product-published" />
          <label htmlFor="published" className="text-sm font-semibold">Published</label>
        </div>
        <Field label="Order"><input type="number" className={inputCls} value={form.order} onChange={(e) => upd("order", Number(e.target.value || 0))} data-testid="product-order" /></Field>
      </section>

      <Repeatable
        title="Features"
        items={form.features}
        onAdd={() => addArrItem("features", { title: "", description: "", icon: "Sparkles" })}
        onRemove={(i) => removeArrItem("features", i)}
        render={(it, i) => (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input className={inputCls} placeholder="Title" value={it.title} onChange={(e) => updArrItem("features", i, "title", e.target.value)} />
            <input className={inputCls} placeholder="Description" value={it.description} onChange={(e) => updArrItem("features", i, "description", e.target.value)} />
            <input className={inputCls} placeholder="Icon (lucide name)" value={it.icon} onChange={(e) => updArrItem("features", i, "icon", e.target.value)} />
          </div>
        )}
      />

      <Repeatable
        title="Screenshots"
        items={form.screenshots}
        onAdd={() => upd("screenshots", [...form.screenshots, ""])}
        onRemove={(i) => upd("screenshots", form.screenshots.filter((_, idx) => idx !== i))}
        render={(it, i) => (
          <ImageUpload label="" value={it} onChange={(v) => updScreenshot(i, v)} />
        )}
      />

      <Repeatable
        title="Pricing plans"
        items={form.pricing}
        onAdd={() => addArrItem("pricing", { name: "", price: "", period: "/month", features: [], highlighted: false, cta_label: "Get Started" })}
        onRemove={(i) => removeArrItem("pricing", i)}
        render={(it, i) => (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input className={inputCls} placeholder="Plan name" value={it.name} onChange={(e) => updArrItem("pricing", i, "name", e.target.value)} />
              <input className={inputCls} placeholder="Price" value={it.price} onChange={(e) => updArrItem("pricing", i, "price", e.target.value)} />
              <input className={inputCls} placeholder="Period" value={it.period} onChange={(e) => updArrItem("pricing", i, "period", e.target.value)} />
              <input className={inputCls} placeholder="CTA label" value={it.cta_label} onChange={(e) => updArrItem("pricing", i, "cta_label", e.target.value)} />
            </div>
            <textarea className={`${inputCls} min-h-[80px]`} placeholder="Features (one per line)" value={(it.features || []).join("\n")} onChange={(e) => updArrItem("pricing", i, "features", e.target.value.split("\n").filter(Boolean))} />
            <label className="text-sm font-semibold flex items-center gap-2">
              <input type="checkbox" checked={!!it.highlighted} onChange={(e) => updArrItem("pricing", i, "highlighted", e.target.checked)} />
              Highlighted
            </label>
          </div>
        )}
      />

      <Repeatable
        title="FAQs"
        items={form.faqs}
        onAdd={() => addArrItem("faqs", { question: "", answer: "" })}
        onRemove={(i) => removeArrItem("faqs", i)}
        render={(it, i) => (
          <div className="space-y-2">
            <input className={inputCls} placeholder="Question" value={it.question} onChange={(e) => updArrItem("faqs", i, "question", e.target.value)} />
            <textarea className={`${inputCls} min-h-[80px]`} placeholder="Answer" value={it.answer} onChange={(e) => updArrItem("faqs", i, "answer", e.target.value)} />
          </div>
        )}
      />

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-zinc-500">Bottom CTA</div>
        <Field label="CTA title"><input className={inputCls} value={form.cta_title} onChange={(e) => upd("cta_title", e.target.value)} /></Field>
        <Field label="CTA button label"><input className={inputCls} value={form.cta_button_label} onChange={(e) => upd("cta_button_label", e.target.value)} /></Field>
        <div className="md:col-span-2">
          <Field label="CTA description"><textarea className={`${inputCls} min-h-[80px]`} value={form.cta_description} onChange={(e) => upd("cta_description", e.target.value)} /></Field>
        </div>
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-zinc-500">SEO Meta</div>
        <Field label="Meta title"><input className={inputCls} value={form.seo.title} onChange={(e) => updSeo("title", e.target.value)} /></Field>
        <Field label="Keywords"><input className={inputCls} value={form.seo.keywords} onChange={(e) => updSeo("keywords", e.target.value)} /></Field>
        <div className="md:col-span-2"><Field label="Meta description"><textarea className={`${inputCls} min-h-[80px]`} value={form.seo.description} onChange={(e) => updSeo("description", e.target.value)} /></Field></div>
        <div className="md:col-span-2"><ImageUpload label="OG image" value={form.seo.og_image} onChange={(v) => updSeo("og_image", v)} testId="product-og-upload" /></div>
        <Field label="OG type" hint="product, website"><input className={inputCls} value={form.seo.og_type} onChange={(e) => updSeo("og_type", e.target.value)} placeholder="product" /></Field>
        <Field label="OG URL"><input className={inputCls} value={form.seo.og_url} onChange={(e) => updSeo("og_url", e.target.value)} /></Field>
        <Field label="Twitter card"><input className={inputCls} value={form.seo.twitter_card} onChange={(e) => updSeo("twitter_card", e.target.value)} placeholder="summary_large_image" /></Field>
        <Field label="Canonical URL"><input className={inputCls} value={form.seo.canonical_url} onChange={(e) => updSeo("canonical_url", e.target.value)} /></Field>
        <div className="md:col-span-2">
          <Field label="JSON-LD Schema" hint="Product or SoftwareApplication schema (JSON object)">
            <textarea className={`${inputCls} min-h-[140px] font-mono text-xs`} value={form.seo.json_ld} onChange={(e) => updSeo("json_ld", e.target.value)} placeholder='{"@context":"https://schema.org","@type":"SoftwareApplication", ...}' />
          </Field>
        </div>
      </section>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-md inline-flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
        </button>
      </div>
    </form>
  );
};

const Repeatable = ({ title, items, onAdd, onRemove, render }) => (
  <section className="bg-white border border-zinc-200 rounded-md p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="text-xs uppercase tracking-widest font-bold text-zinc-500">{title}</div>
      <button type="button" onClick={onAdd} className="text-orange-600 hover:text-orange-700 text-sm font-semibold inline-flex items-center gap-1"><Plus size={14} /> Add</button>
    </div>
    <div className="space-y-4">
      {items.map((it, i) => (
        <div key={i} className="border border-zinc-100 rounded-md p-4 bg-zinc-50/40">
          <div className="flex items-start gap-3">
            <div className="flex-1">{render(it, i)}</div>
            <button type="button" onClick={() => onRemove(i)} className="p-2 rounded-md text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="text-sm text-zinc-400">None added.</div>}
    </div>
  </section>
);

export default AdminProductEditor;
